/**
 * AuthContext
 * 
 * Thin wrapper around SessionContext for backwards compatibility.
 * All auth logic is now handled by SessionContext.
 * 
 * This file exists to avoid breaking existing imports of:
 * - AuthProvider
 * - useAuth
 * 
 * New code should use useSession() or useSessionAuth() directly.
 */

import { ReactNode } from 'react';
import { useSession } from './SessionContext';

// Re-export types
export type { SessionUser as User } from './SessionContext';

/**
 * AuthProvider is now just an alias for SessionProvider
 * This maintains backwards compatibility with existing App.tsx structure
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // AuthProvider is now a pass-through - SessionProvider handles everything
  // This is here for backwards compatibility only
  // In App.tsx, we'll use SessionProvider directly
  return <>{children}</>;
}

/**
 * useAuth hook - backwards compatible wrapper around useSession
 */
export function useAuth() {
  const session = useSession();
  
  return {
    user: session.user,
    isAuthenticated: session.isAuthenticated,
    isLoading: session.isLoading,
    login: session.login,
    logout: session.logout,
    refreshUser: session.refreshUser,
  };
}
